import axios, { AxiosError, type AxiosInstance } from 'axios';
import { firebaseAuth } from '@/core/auth/firebase';
import { getActiveCompanyId } from '@/core/tenant/TenantContext';

export const http: AxiosInstance = axios.create({
  baseURL: import.meta.env.VITE_API_BASE_URL,
  timeout: 20_000,
});

// Attach the Firebase ID token to every request.
http.interceptors.request.use(async (config) => {
  const user = firebaseAuth.currentUser;
  if (user) {
    // getIdToken auto-refreshes when close to expiry
    const token = await user.getIdToken();
    config.headers.set('Authorization', `Bearer ${token}`);
  }
  // Tenant the request operates on. The backend ignores it unless the caller
  // is `empresa`, and rejects it outright for anyone else pointing at a
  // foreign tenant — so a stale value can never write into the wrong company.
  const companyId = getActiveCompanyId();
  if (companyId) {
    config.headers.set('X-Company-Id', companyId);
  }
  return config;
});

// On 401, force a token refresh and retry once.
http.interceptors.response.use(
  (r) => r,
  async (error: AxiosError) => {
    const original = error.config as (typeof error.config & { _retry?: boolean }) | undefined;
    if (error.response?.status === 401 && original && !original._retry) {
      const user = firebaseAuth.currentUser;
      if (user) {
        original._retry = true;
        await user.getIdToken(true); // force refresh
        return http.request(original);
      }
    }
    return Promise.reject(error);
  },
);
